import {
  useRef,
  useMemo,
  useEffect,
  useEffectEvent,
  useLayoutEffect,
} from "react";
import DOMPurify from "dompurify";
import parse from "html-react-parser";
import { sanitizeCss, scopeCss } from "./utils/helpers.ts";
import { useDocumentLinks } from "./hooks/useDocumentLinks.ts";
import { useSelection } from "./hooks/useSelection.ts";
import {
  useScrollAndHighlight,
  type ReferenceHighlightName,
} from "./hooks/useScrollAndHighlight.ts";
import { useContentRendered } from "./hooks/useContentRendered.ts";
import { useDocumentScrollMemory } from "./hooks/useDocumentScrollMemory.ts";
import { useDocumentLinkHandlers } from "./hooks/useDocumentLinkHandlers.ts";
import { useWarmSkippedPages } from "./hooks/useWarmSkippedPages.ts";
import { SANITIZE_HTML_OPTIONS } from "./utils/sanitizeOptions.ts";
import { createHtmlParserOptions } from "./utils/createHtmlParserOptions.tsx";
import {
  buildDocumentLinkInjectionPlan,
  coerceInjectionPlan,
} from "./utils/documentLinkInjectionPlan";
import { logDocumentLinkInjectionDevReport } from "./utils/documentLinkInjectionDevReport";
import { DocumentStyles } from "./components/DocumentStyles.tsx";
import { DocumentWatermark } from "./components/DocumentWatermark.tsx";
import { SelectionButton } from "./components/SelectionButton.tsx";
import { DocumentLinksLoadingContext } from "./components/DocumentLinksLoadingContext";
import { getInvalidLabel } from "@/config/datasources";
import { useReferenceSidebarHoverPreviewStore } from "@/stores/reference-sidebar-hover-preview.store";
import { useReferencesSidebarFiltersStore } from "@/stores/references-sidebar-filters.store";
import { REFERENCE_SIDEBAR_PREVIEW_BLOCK_CLASS } from "./utils/referenceHighlightConstants";
import {
  buildWorkspaceReferenceVisibilityContext,
  collectDocumentWorkspaceReferenceLinks,
} from "@/utils/documentLinkWorkspaceVersionCollapse";
import { groupChangedContainersInDocumentOrder } from "@/hooks/useConsolidationChangeNavigation";
import {
  CONSOLIDATION_CHANGE_GROUP_CLASS,
  wrapGroupedChangedContainersHtml,
} from "./utils/consolidationChangeGrouping";
import { injectDeletedContainerGhostsHtml } from "./utils/consolidationDeletedGhosts";
import { EMPTY_CONTAINER_IDS } from "./utils/stableEmptyProps";
import { DeletedContainerChange } from "@/models/consolidation";
import { ConsolidationChangeStatus } from "@/models/legislation/metadata/ConsolidationMetadata";
import type { ConsolidationRenderContext } from "@/hooks/useLegislationViewerContent";

/** Toggled on `.doc-root` to reveal deleted "ghost" containers when highlighting. */
const CONSOLIDATION_HIGHLIGHTS_ON_CLASS = "consolidation-highlights-on";

/**
 * Toggled on `.doc-root` while the user is drag-selecting text. CSS uses it to
 * drop the pointer/hover affordance on changed sections so selecting text (to
 * create a reference) wins over the amendment click/hover interaction.
 */
const CONSOLIDATION_SELECTING_CLASS = "consolidation-selecting";

/** Pixels the pointer must move after mousedown before we treat it as a drag. */
const DRAG_THRESHOLD_PX = 4;

/** Marks a changed section whose amendment is still an unverified draft. */
const CONSOLIDATION_CHANGE_DRAFT_CLASS = "consolidation-change-draft";

export type DocumentRendererProps = {
  rawHTML: string;
  /**
   * The caller already ran DOMPurify over `rawHTML` with its default config
   * (as strict on URIs as SANITIZE_HTML_OPTIONS, which only adds `blob:`), so
   * the renderer skips its own pass. Opt-in: callers that hand over
   * unsanitized markup (legislation compare mode) must leave it unset.
   */
  rawHtmlIsSanitized?: boolean;
  rawCSS?: string;
  highlightIds?: string[];
  scrollToId?: string;
  currentDocId?: string;
  currentSource?: string;
  currentWorkspaceId?: string;
  /**
   * Stable identity of the document, enabling scroll-position memory across page
   * loads (LAR-361). Opt-in: renders that must not participate — the print copy,
   * sidebar link previews — simply omit it.
   */
  scrollMemoryKey?: string;
  className?: string;
  shouldGenerateDarkModeCss?: boolean;
  isCurrent?: boolean | null;
  isProcessingIncomplete?: boolean;
  /** When true, watermark uses a fixed dark color so it stays visible on white in dark mode (e.g. workspace docs that doesn't change color depending on theme). */
  watermarkFixedColor?: boolean;
  /** Zoom level as percentage (100 = 100%) */
  zoomLevel?: number;
  /** When opening a document link / reference: the passage to highlight inside the highlighted blocks */
  highlightMatchText?: string;
  highlightChunkText?: string;
  /** Consolidation API render — container ids are injected; links/highlights stay enabled. */
  isConsolidatedRender?: boolean;
  /**
   * Identifies the consolidation snapshot on screen so references created here
   * can point back to it instead of the plain anchor LBK/LOVH text.
   */
  consolidationContext?: ConsolidationRenderContext | null;
  /** Container ids with consolidation amendments for the active render scope. */
  changedContainerIds?: ReadonlySet<string>;
  /**
   * Maps each changed container to its amendment-point group key. Consecutive
   * containers sharing a key are rendered as one changed section (single box,
   * single click target).
   */
  changedContainerGroupKeys?: ReadonlyMap<string, string>;
  /**
   * Per-container approval status. The viewer always renders the full draft, so
   * each changed container is annotated with whether its own amendment is
   * verified (`APPROVED`) or still an unverified draft (`NEED_REVIEW`).
   */
  changedContainerStatusById?: ReadonlyMap<string, ConsolidationChangeStatus>;
  /**
   * Containers deleted at the rendered date. Re-inserted as strikethrough
   * "ghosts" at their blueprint position; hidden unless highlighting is on.
   */
  deletedContainerChanges?: readonly DeletedContainerChange[];
  /** When true, visually marks amended consolidation containers in the document. */
  highlightConsolidationChanges?: boolean;
  /** Opens the instruction panel when the user clicks an amended container. */
  onChangedContainerClick?: (container: {
    id: string;
    type: string;
  }) => void;
  /** When set, applies pinpoint diff highlights inside the selected container. */
  containerAmendmentReview?: {
    containerId: string;
    diffHtml: string | null;
  } | null;
  /**
   * Called with `.doc-root` once per commit that may have replaced document
   * text nodes: a new parse (first render, references arriving, compare mode,
   * consolidation HTML, amendment review), or the references sidebar's
   * visibility toggles swapping reference anchors between `<a>` and `<span>`.
   * Not called when a re-render leaves the text nodes alone (the links-loading
   * flag flipping). Runs in a layout effect, before paint, so a feature can
   * rebuild its highlight ranges before stale ones paint the wrong characters
   * (ADR 0001). Runs twice on mount under Strict Mode; handlers must be
   * idempotent.
   */
  onContentCommitted?: (docRoot: HTMLElement) => void;
};

function DocumentRenderer({
  rawHTML,
  rawHtmlIsSanitized = false,
  rawCSS = "",
  highlightIds,
  scrollToId,
  className,
  currentSource,
  currentDocId,
  currentWorkspaceId,
  scrollMemoryKey,
  shouldGenerateDarkModeCss = true,
  isCurrent = true,
  isProcessingIncomplete = false,
  watermarkFixedColor = false,
  zoomLevel = 100,
  highlightMatchText,
  highlightChunkText,
  isConsolidatedRender = false,
  consolidationContext = null,
  changedContainerIds = EMPTY_CONTAINER_IDS,
  changedContainerGroupKeys,
  changedContainerStatusById,
  deletedContainerChanges,
  highlightConsolidationChanges = false,
  onChangedContainerClick,
  containerAmendmentReview = null,
  onContentCommitted,
}: DocumentRendererProps) {
  const docRootRef = useRef<HTMLDivElement | null>(null);
  const lastScrolledContainerIdRef = useRef<string | null>(null);
  // A drag-select ends with a trailing `click`; this flag lets the amendment
  // click handler ignore that click so it doesn't open the instruction panel.
  const didDragRef = useRef(false);
  const dragStartPosRef = useRef<{ x: number; y: number } | null>(null);
  const featuresDisabled = isProcessingIncomplete && !isConsolidatedRender;

  const sidebarHoverActive = useReferenceSidebarHoverPreviewStore(
    (s) => s.sidebarHoverActive
  );
  const previewOpenDocumentId = useReferenceSidebarHoverPreviewStore(
    (s) => s.openDocumentId
  );
  const previewMatchText = useReferenceSidebarHoverPreviewStore(
    (s) => s.previewMatchText
  );
  const previewChunkText = useReferenceSidebarHoverPreviewStore(
    (s) => s.previewChunkText
  );
  const previewHighlightIds = useReferenceSidebarHoverPreviewStore(
    (s) => s.previewHighlightIds
  );
  const previewScrollToMatch = useReferenceSidebarHoverPreviewStore(
    (s) => s.previewScrollToMatch
  );
  const contentHoverDocLinkGroupId = useReferenceSidebarHoverPreviewStore(
    (s) => s.contentHoverDocLinkGroupId
  );

  const isSidebarPreviewActive =
    !featuresDisabled &&
    !!currentDocId &&
    sidebarHoverActive &&
    previewOpenDocumentId === currentDocId;

  const previewHasPassage = Boolean(previewMatchText?.trim());

  const filteredPreviewBlockIds =
    previewHighlightIds?.filter((id): id is string => Boolean(id)) ?? [];

  const effectiveHighlightIds = isSidebarPreviewActive
    ? previewHasPassage
      ? []
      : filteredPreviewBlockIds.length > 0
        ? filteredPreviewBlockIds
        : []
    : highlightIds;

  const effectiveScrollToId = isSidebarPreviewActive ? undefined : scrollToId;

  const effectiveHighlightMatchText = isSidebarPreviewActive
    ? previewMatchText?.trim() || undefined
    : highlightMatchText;

  /** Omit chunk when using block-id-only preview so useScrollAndHighlight does not suppress blocks without a passage. */
  const effectiveHighlightChunkText = isSidebarPreviewActive
    ? previewHasPassage
      ? previewChunkText?.trim() || undefined
      : undefined
    : highlightChunkText;

  const referenceHighlightName: ReferenceHighlightName =
    isSidebarPreviewActive && effectiveHighlightMatchText
      ? "reference-preview"
      : "reference";

  const sidebarBlockHighlightClass =
    isSidebarPreviewActive &&
    !previewHasPassage &&
    filteredPreviewBlockIds.length > 0
      ? REFERENCE_SIDEBAR_PREVIEW_BLOCK_CLASS
      : undefined;

  const suppressScrollToMatchPreview =
    isSidebarPreviewActive && !previewScrollToMatch;

  // Document links - disable when processing incomplete
  const {
    documentLinks,
    documentLinksMap,
    isLoadingDocumentLinks,
    extractDocumentLinkKeys,
    resolveDocumentLink,
    navigateToDocumentLink,
    buildDocumentLinkHref,
  } = useDocumentLinks(featuresDisabled ? undefined : currentDocId);

  // Selection - keep enabled for text selection, but hide button when processing incomplete
  const {
    selectedDivIds,
    destinationUniqueMatchText,
    destinationUniqueChunkText,
    buttonPosition,
    buttonRef,
    clearSelection,
  } = useSelection();

  // Sanitize once per document. Keyed on the HTML alone so that no other prop,
  // and no re-render, can trigger another pass over a multi-megabyte document
  // (LAR-487). Consolidation HTML is sanitized in processConsolidatedHtml, and
  // callers that already ran DOMPurify say so with `rawHtmlIsSanitized`.
  const sanitizedHtml = useMemo(
    () =>
      isConsolidatedRender || rawHtmlIsSanitized
        ? rawHTML
        : DOMPurify.sanitize(rawHTML, SANITIZE_HTML_OPTIONS),
    [rawHTML, isConsolidatedRender, rawHtmlIsSanitized]
  );

  const safeHtml = useMemo(() => {
    if (!isConsolidatedRender) return sanitizedHtml;

    // Re-insert deleted containers as ghosts at their blueprint position first,
    // so contiguous deletions (and adjacent same-point inserts) group together.
    let html = injectDeletedContainerGhostsHtml(
      sanitizedHtml,
      deletedContainerChanges
    );

    if (changedContainerGroupKeys) {
      // Wrap contiguous containers from one amendment point so the group renders
      // as a single changed section. Injected before parse() so React owns the
      // wrapper nodes.
      html = wrapGroupedChangedContainersHtml(
        html,
        changedContainerIds,
        changedContainerGroupKeys
      );
    }
    return html;
  }, [
    sanitizedHtml,
    isConsolidatedRender,
    changedContainerIds,
    changedContainerGroupKeys,
    deletedContainerChanges,
  ]);

  const isContentRendered = useContentRendered(docRootRef, safeHtml);

  const injectionBuild = useMemo(
    () =>
      buildDocumentLinkInjectionPlan(
        safeHtml,
        documentLinksMap,
        currentDocId
      ),
    [safeHtml, documentLinksMap, currentDocId]
  );

  const injectionPlan = coerceInjectionPlan(injectionBuild);

  const workspaceVisibilityContext = useMemo(() => {
    if (!currentDocId || !documentLinks?.length) {
      return undefined;
    }
    const workspaceRefs = collectDocumentWorkspaceReferenceLinks(
      documentLinks,
      currentDocId
    );
    if (workspaceRefs.length === 0) {
      return undefined;
    }
    return buildWorkspaceReferenceVisibilityContext(
      workspaceRefs,
      currentDocId
    );
  }, [currentDocId, documentLinks]);

  /** See `documentLinkInjectionDevReport.ts` — set `VITE_DOC_LINK_INJECTION_DEBUG=true` in `.env.local`. */
  const docLinkInjectionDevLogEnabled =
    import.meta.env.DEV &&
    (import.meta.env.VITE_DOC_LINK_INJECTION_DEBUG === "true" ||
      import.meta.env.VITE_DOC_LINK_INJECTION_DEBUG === "1");

  const referencesSidebarShowHistorical = useReferencesSidebarFiltersStore(
    (s) => s.showHistorical
  );
  const referencesSidebarShowEarlierWorkspace =
    useReferencesSidebarFiltersStore((s) => s.showEarlierWorkspaceVersions);

  useEffect(() => {
    if (
      !docLinkInjectionDevLogEnabled ||
      isLoadingDocumentLinks ||
      featuresDisabled ||
      !currentDocId
    ) {
      return;
    }
    logDocumentLinkInjectionDevReport({
      documentId: currentDocId,
      report: injectionBuild.report,
      documentLinksMap,
      referencesSidebarToggles: {
        showHistorical: referencesSidebarShowHistorical,
        showEarlierWorkspaceVersions: referencesSidebarShowEarlierWorkspace,
      },
    });
  }, [
    docLinkInjectionDevLogEnabled,
    isLoadingDocumentLinks,
    featuresDisabled,
    currentDocId,
    injectionBuild.report,
    documentLinksMap,
    referencesSidebarShowHistorical,
    referencesSidebarShowEarlierWorkspace,
  ]);

  useEffect(() => {
    const docRoot = docRootRef.current;
    if (!docRoot || !isContentRendered || !isConsolidatedRender) return;

    // Clear previous marks on both containers and group wrappers so the effect
    // is idempotent across re-runs.
    docRoot
      .querySelectorAll<HTMLElement>(
        `[data-container-type][id], .${CONSOLIDATION_CHANGE_GROUP_CLASS}`
      )
      .forEach((el) => {
        el.classList.remove(
          "consolidation-changed",
          "consolidation-changed-clickable",
          CONSOLIDATION_CHANGE_DRAFT_CLASS
        );
        el.removeAttribute("data-approval");
      });

    const groups = groupChangedContainersInDocumentOrder(
      docRoot,
      changedContainerIds,
      changedContainerGroupKeys
    );

    groups.forEach((members) => {
      // A multi-container amendment point is wrapped (in safeHtml) so the whole
      // run is one pressable box; single containers are their own section.
      const section =
        (members.length > 1
          ? members[0].closest<HTMLElement>(
              `.${CONSOLIDATION_CHANGE_GROUP_CLASS}`
            )
          : null) ?? members[0];

      // Deleted-only sections get their own red strikethrough styling (CSS);
      // skip the blue "changed" highlight so the two never fight. Mixed groups
      // (a deletion next to a live insert) keep the blue box around them.
      const isDeletedOnly = members.every((m) =>
        m.hasAttribute("data-consolidation-deleted")
      );

      if (highlightConsolidationChanges && !isDeletedOnly) {
        section.classList.add("consolidation-changed");
      }
      if (onChangedContainerClick) {
        section.classList.add("consolidation-changed-clickable");
      }

      // Per-change approval annotation. The viewer always renders the full
      // draft, so each changed section carries its own verification state — a
      // persistent trust signal that stays on regardless of the "Fremhæv
      // ændringer" toggle. A section counts as verified only when every one of
      // its members is APPROVED; anything unverified is flagged as a draft.
      if (changedContainerStatusById) {
        const allApproved = members.every(
          (m) => changedContainerStatusById.get(m.id) === "APPROVED"
        );
        section.setAttribute(
          "data-approval",
          allApproved ? "approved" : "need-review"
        );
        // Deleted-only ghosts already have distinct red strikethrough styling;
        // don't stack the draft accent border on top of it.
        if (!allApproved && !isDeletedOnly) {
          section.classList.add(CONSOLIDATION_CHANGE_DRAFT_CLASS);
        }
      }
    });
  }, [
    changedContainerIds,
    changedContainerGroupKeys,
    changedContainerStatusById,
    highlightConsolidationChanges,
    isConsolidatedRender,
    isContentRendered,
    onChangedContainerClick,
    safeHtml,
  ]);

  // Gate deleted-ghost visibility on the highlight toggle. Ghosts are always in
  // the DOM (so grouping/click stay stable), but only shown when highlighting.
  useEffect(() => {
    const docRoot = docRootRef.current;
    if (!docRoot) return;
    docRoot.classList.toggle(
      CONSOLIDATION_HIGHLIGHTS_ON_CLASS,
      isConsolidatedRender && highlightConsolidationChanges
    );
  }, [highlightConsolidationChanges, isConsolidatedRender, isContentRendered]);

  // Desktop-only drag tracking. Records whether the pointer actually dragged
  // between mousedown and mouseup (a text selection) and, while dragging, marks
  // `.doc-root` so CSS drops the changed-section pointer/hover affordance. The
  // `didDragRef` flag is consumed by the amendment click handler below.
  useEffect(() => {
    const docRoot = docRootRef.current;
    if (!docRoot || !isContentRendered) return;

    let rafId: number | null = null;

    const handleMouseDown = (event: MouseEvent) => {
      if (event.button !== 0) return;
      dragStartPosRef.current = { x: event.clientX, y: event.clientY };
      didDragRef.current = false;
    };

    const handleMouseMove = (event: MouseEvent) => {
      const start = dragStartPosRef.current;
      if (!start || didDragRef.current) return;
      const movedFarEnough =
        Math.abs(event.clientX - start.x) > DRAG_THRESHOLD_PX ||
        Math.abs(event.clientY - start.y) > DRAG_THRESHOLD_PX;
      if (movedFarEnough) {
        didDragRef.current = true;
        docRoot.classList.add(CONSOLIDATION_SELECTING_CLASS);
      }
    };

    const handleMouseUp = () => {
      dragStartPosRef.current = null;
      // Remove the class on the next frame so the trailing `click` still sees a
      // stable state; `didDragRef` stays set for the click handler to consume.
      rafId = requestAnimationFrame(() => {
        docRoot.classList.remove(CONSOLIDATION_SELECTING_CLASS);
      });
    };

    docRoot.addEventListener("mousedown", handleMouseDown);
    // Pointer can leave `.doc-root` during a drag-select; track move/up on window.
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      docRoot.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [isContentRendered]);

  useEffect(() => {
    const docRoot = docRootRef.current;
    if (!docRoot || !isContentRendered || !onChangedContainerClick) return;

    const handleClick = (event: MouseEvent) => {
      // The trailing click of a drag-select must not open the amendment panel —
      // consume the flag and bail so the reference popup survives.
      if (didDragRef.current) {
        didDragRef.current = false;
        return;
      }
      const node = event.target as HTMLElement | null;
      // Inline links own their own click (navigation). The native doc-root
      // listener runs before React's delegated onClick during bubbling, so the
      // anchor's stopPropagation can't reach us — guard here instead.
      if (node?.closest("a")) return;
      const section = node?.closest<HTMLElement>(
        ".consolidation-changed-clickable"
      );
      if (!section || !docRoot.contains(section)) return;

      // A grouped section carries the representative on the wrapper; a single
      // container is its own target.
      const isGroup = section.classList.contains(
        CONSOLIDATION_CHANGE_GROUP_CLASS
      );
      onChangedContainerClick({
        id: isGroup
          ? section.getAttribute("data-consolidation-representative-id") ?? ""
          : section.id,
        type: isGroup
          ? section.getAttribute("data-consolidation-representative-type") ?? ""
          : section.getAttribute("data-container-type") ?? "",
      });
    };

    docRoot.addEventListener("click", handleClick);
    return () => docRoot.removeEventListener("click", handleClick);
  }, [docRootRef, isContentRendered, onChangedContainerClick, changedContainerIds]);

  // The amendment-review diff is injected as React-owned markup via
  // dangerouslySetInnerHTML in the parser (see `activeAmendmentDiffHtml` below
  // and createHtmlParserOptions). This effect only applies the selection
  // outline and the one-off scroll nudge — it must never mutate innerHTML.
  //
  // Historically the diff was written directly onto the container's innerHTML,
  // which destroyed the parsed child nodes React still owned. On the next
  // consolidation switch React reconciled those nodes and called removeChild on
  // markup that no longer existed, throwing "NotFoundError: The node to be
  // removed is not a child of this node." (surfaced as the generic React Router
  // "Unexpected Application Error"). Letting React own the diff removes that
  // whole class of bug. See LAR-306.
  useLayoutEffect(() => {
    const docRoot = docRootRef.current;
    if (!docRoot || !isContentRendered) return;

    // Idempotent: clear any prior outline before re-applying.
    docRoot
      .querySelectorAll<HTMLElement>(".consolidation-change-selected")
      .forEach((el) => el.classList.remove("consolidation-change-selected"));

    if (!containerAmendmentReview) {
      lastScrolledContainerIdRef.current = null;
      return;
    }

    const { containerId } = containerAmendmentReview;
    const activeContainer = docRoot.querySelector<HTMLElement>(
      `#${CSS.escape(containerId)}`
    );

    if (!activeContainer) return;

    // Outline the whole section: a grouped amendment point wraps its containers,
    // so select the wrapper when present, otherwise the container itself.
    const selectionTarget =
      activeContainer.closest<HTMLElement>(
        `.${CONSOLIDATION_CHANGE_GROUP_CLASS}`
      ) ?? activeContainer;
    selectionTarget.classList.add("consolidation-change-selected");

    // Only nudge the selected container into view the first time it is
    // selected (not when diffHtml arrives async), and only if it is off-screen,
    // so opening the panel never yanks the reader away from their place.
    if (lastScrolledContainerIdRef.current !== containerId) {
      lastScrolledContainerIdRef.current = containerId;
      const rect = activeContainer.getBoundingClientRect();
      const offscreen = rect.top < 0 || rect.bottom > window.innerHeight;
      if (offscreen) {
        activeContainer.scrollIntoView({ block: "nearest" });
      }
    }
  }, [containerAmendmentReview, isContentRendered, safeHtml]);

  // CSS processing
  const safeScopedCss = useMemo(
    () => scopeCss(sanitizeCss(rawCSS), ".doc-root"),
    [rawCSS]
  );

  // Sanitized diff markup for the container under review, injected by the
  // parser as React-owned innerHTML (see the content memo). Null when no review
  // is open or the diff has not yet loaded, in which case the container renders
  // its normal parsed children.
  const activeAmendmentContainerId = containerAmendmentReview?.containerId;
  const activeAmendmentDiffHtml = useMemo(() => {
    const diffHtml = containerAmendmentReview?.diffHtml;
    if (!diffHtml) return null;
    return DOMPurify.sanitize(diffHtml, SANITIZE_HTML_OPTIONS);
  }, [containerAmendmentReview?.diffHtml]);

  // Parse content
  // CRITICAL FIX: Create fresh options object inside content memoization to ensure each parse() call
  // gets its own processedLinks Set. This prevents multiple parse() calls from sharing state.
  const content = useMemo(
    () => {
      const options = createHtmlParserOptions({
        extractDocumentLinkKeys,
        resolveDocumentLink,
        navigateToDocumentLink,
        buildDocumentLinkHref,
        currentDocumentId: currentDocId,
        injectionPlan,
        allDocumentLinks: documentLinks,
        workspaceVisibilityContext,
        activeAmendmentContainerId,
        activeAmendmentDiffHtml,
      });

      return parse(safeHtml, options);
    },
    // Every dependency here re-parses the whole document. The links loading
    // flag deliberately is not one: anchors read it from
    // DocumentLinksLoadingContext, so finishing loading costs a few anchor
    // re-renders instead of a full re-parse (LAR-487).
    [
      safeHtml,
      navigateToDocumentLink,
      buildDocumentLinkHref,
      extractDocumentLinkKeys,
      resolveDocumentLink,
      currentDocId,
      injectionPlan,
      documentLinks,
      workspaceVisibilityContext,
      activeAmendmentContainerId,
      activeAmendmentDiffHtml,
    ]
  );

  // A new value whenever React may have replaced the document's text nodes,
  // which leaves highlight ranges over them painting nothing: a new parse, or
  // the visibility toggles, on which ParsedDocumentLinkAnchor and
  // MultiLinkAnchor swap between <a> and <span> without one.
  const renderedText = useMemo(
    () => ({
      content,
      referencesSidebarShowHistorical,
      referencesSidebarShowEarlierWorkspace,
    }),
    [
      content,
      referencesSidebarShowHistorical,
      referencesSidebarShowEarlierWorkspace,
    ]
  );

  // Keyed on the rendered text alone: a new callback identity must not count
  // as new text.
  const announceContentCommitted = useEffectEvent((docRoot: HTMLElement) => {
    onContentCommitted?.(docRoot);
  });

  useLayoutEffect(() => {
    const docRoot = docRootRef.current;
    if (!docRoot) return;
    announceContentCommitted(docRoot);
  }, [renderedText]);

  // Document link handlers
  useDocumentLinkHandlers({
    docRootRef,
    isContentRendered,
    documentLinksMap,
    extractDocumentLinkKeys,
    resolveDocumentLink,
    navigateToDocumentLink,
    isLoadingDocumentLinks,
  });

  useEffect(() => {
    const root = docRootRef.current;
    if (!root) return;
    root
      .querySelectorAll("a.doc-link-anchor.doc-link-group-active")
      .forEach((el) => {
        el.classList.remove("doc-link-group-active");
      });
    const gid = contentHoverDocLinkGroupId;
    if (!gid) return;
    root
      .querySelectorAll("a.doc-link-anchor[data-doc-link-group]")
      .forEach((el) => {
        if (el.getAttribute("data-doc-link-group") === gid) {
          el.classList.add("doc-link-group-active");
        }
      });
  }, [contentHoverDocLinkGroupId, isContentRendered]);

  // Scroll and highlight - disable when processing incomplete
  useScrollAndHighlight(
    docRootRef,
    renderedText,
    isContentRendered,
    featuresDisabled ? [] : effectiveHighlightIds,
    featuresDisabled ? undefined : effectiveScrollToId,
    featuresDisabled ? undefined : effectiveHighlightMatchText,
    featuresDisabled ? undefined : effectiveHighlightChunkText,
    referenceHighlightName,
    featuresDisabled ? undefined : sidebarBlockHighlightClass,
    suppressScrollToMatchPreview
  );

  useDocumentScrollMemory(
    docRootRef,
    isContentRendered,
    scrollMemoryKey,
    Boolean(
      effectiveScrollToId ||
        effectiveHighlightMatchText ||
        effectiveHighlightChunkText
    )
  );

  useWarmSkippedPages(docRootRef, isContentRendered, safeHtml);

  return (
    <div className={className}>
      <DocumentStyles
        safeScopedCss={safeScopedCss}
        shouldGenerateDarkModeCss={shouldGenerateDarkModeCss}
      />
      <div className="relative">
        <div
          className="doc-root"
          ref={docRootRef}
          style={
            zoomLevel !== 100
              ? {
                  transform: `scale(${zoomLevel / 100})`,
                  transformOrigin: "top left",
                  width: `${100 / (zoomLevel / 100)}%`,
                }
              : undefined
          }
        >
          <DocumentLinksLoadingContext.Provider value={isLoadingDocumentLinks}>
            {content}
          </DocumentLinksLoadingContext.Provider>
        </div>
        {!isCurrent && (
          <DocumentWatermark
            label={getInvalidLabel(currentSource ?? "")}
            fixedColor={watermarkFixedColor}
            docRootRef={docRootRef}
          />
        )}
      </div>
      {!featuresDisabled && (
        <SelectionButton
          selectedDivIds={selectedDivIds}
          destinationUniqueMatchText={destinationUniqueMatchText}
          destinationUniqueChunkText={destinationUniqueChunkText}
          buttonPosition={buttonPosition}
          buttonRef={buttonRef}
          currentSource={currentSource}
          currentDocId={currentDocId}
          currentWorkspaceId={currentWorkspaceId}
          consolidationContext={consolidationContext}
          onClearSelection={clearSelection}
        />
      )}
    </div>
  );
}

export default DocumentRenderer;
